import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { AuthTokenService } from './auth-token.service';
import { environment } from '@env/environment';
import { isTokenExpired } from '@app/modules/auth/utils/token-expiry';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private tokenService: AuthTokenService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip authentication for login and refresh endpoints
    if (this.isAuthEndpoint(req.url)) {
      return next.handle(req);
    }

    // Never attach a token we already know is dead. Sending an expired one
    // turns PUBLIC routes — the break-glass QR pages — into 401s, which then
    // drag them through the refresh machinery for no reason. A request with no
    // Authorization header is exactly what those routes expect.
    const token = this.tokenService.getAccessToken();
    if (token && !isTokenExpired(token)) {
      req = this.addToken(req, token);
    }

    return next.handle(req).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(req, next);
        }
        return throwError(error);
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap(token => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token);
          return next.handle(this.addToken(request, token));
        }),
        catchError(error => {
          this.isRefreshing = false;
          this.tokenService.clearTokens();

          // Release everything queued behind this refresh.
          //
          // Without this, every request that arrived while the refresh was in
          // flight sat on `refreshTokenSubject` waiting for a token that was
          // never going to come — no error, no timeout, just a spinner that
          // never stops. Failing them is the honest outcome: the session is
          // gone, and the page can say so.
          const waiting = this.refreshTokenSubject;
          this.refreshTokenSubject = new BehaviorSubject<string | null>(null);
          waiting.error(error);

          return throwError(error);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next.handle(this.addToken(request, token!)))
    );
  }

  private isAuthEndpoint(url: string): boolean {
    return [
      `${environment.apiUrl}/accounts/login`,
      `${environment.apiUrl}/accounts/refresh`
    ].some(endpoint => url.startsWith(endpoint));
  }
}